import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useStyles } from './styles';
import { CustomSpotsEditorController } from './useCustomSpotsEditor';

type EditorDialogsProps = {
  classes: ReturnType<typeof useStyles>;
  controller: CustomSpotsEditorController;
};

export const EditorDialogs: React.FC<EditorDialogsProps> = ({
  classes,
  controller,
}) => {
  const {
    t,
    isFailureOpen,
    setIsFailureOpen,
    failureDetailsOpen,
    setFailureDetailsOpen,
    failureMessage,
    unsupportedImageFormatText,
    requirementsText,
    handleTryAgain,
    isAssetUpdatedOpen,
    handleSaveAssetUpdated,
    handleRevertAssetUpdated,
    isReverting,
  } = controller;

  return (
    <>
      <Dialog
        open={isFailureOpen}
        onClose={() => setIsFailureOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: classes.failurePaper }}
      >
        <DialogTitle sx={classes.failureTitle}>
          {t('customSpotsEditor.failureTitle')}
          <IconButton
            sx={classes.failureCloseButton}
            onClick={() => setIsFailureOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={classes.failureContent}>
          <Typography sx={classes.failureText}>
            {failureMessage || unsupportedImageFormatText}
          </Typography>
          {failureDetailsOpen ? (
            <Typography sx={classes.failureDetails}>
              {requirementsText}
            </Typography>
          ) : null}
          <Box sx={classes.failureActions}>
            <Button
              variant="outlined"
              onClick={() => setFailureDetailsOpen((v) => !v)}
              sx={classes.failureMoreInfoButton}
            >
              {t('customSpotsEditor.moreInfo')}
            </Button>
            <Button
              variant="contained"
              onClick={handleTryAgain}
              sx={classes.failureTryAgainButton}
            >
              {t('customSpotsEditor.tryAgain')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isAssetUpdatedOpen}
        onClose={handleSaveAssetUpdated}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: classes.assetUpdatedPaper }}
      >
        <DialogTitle sx={classes.assetUpdatedTitle}>
          {t('customSpotsEditor.assetUpdatedTitle')}
          <IconButton
            sx={classes.assetUpdatedCloseButton}
            onClick={handleSaveAssetUpdated}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={classes.assetUpdatedContent}>
          <Typography sx={classes.assetUpdatedText}>
            {t('customSpotsEditor.assetUpdatedText')}
          </Typography>
          <Box sx={classes.assetUpdatedActions}>
            <Button
              variant="outlined"
              onClick={handleRevertAssetUpdated}
              disabled={isReverting}
              sx={classes.assetUpdatedRevertButton}
            >
              {t('customSpotsEditor.revert')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAssetUpdated}
              disabled={isReverting}
              sx={classes.assetUpdatedSaveButton}
            >
              {t('common.save')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
